import React, { useState } from "react";
import {
  ActivityIndicator,
  Alert,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useRouter } from "expo-router";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
// import analytics from "@react-native-firebase/analytics";

import TermsOfService from "../Legal/TermsOfService";

import EmailInput from "./Components/EmailInput";
import PasswordInput from "./Components/PasswordInput";
import PasswordWithTooltip from "./Components/PasswordWithTooltip";
import UsernameInput from "./Components/UsernameInput";
import UsernameWithTooltip from "./Components/UsernameWithTooltip";

import { ApiError, ApiErrorType } from "@/schemas/api/error";
import { isValidEmailAddress } from "@/schemas/forms/email";
import { isValidPassword } from "@/schemas/forms/password";
import { isValidUsername } from "@/schemas/forms/username";
import AuthService from "@/services/internal/auth";
import { alertUnexpectedApiError } from "@/utils/alerts";
import type { LoginCredentials } from "@/schemas/auth/login";
import { setLoginCredentialsInStorage } from "@/hooks/authentication";
import { getDeviceIdentifier, logDeviceData } from "@/hooks/device";
import { useLocationPermission } from "@/hooks/location";
import { useUpdateInfo } from "@/screens/Settings/Troubleshooting/Services/utils/fetchUpdateInfo";

function SignupForm(): React.JSX.Element {
  const router = useRouter();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [username, setUsername] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
  const { location } = useLocationPermission();
  const updatesInfo = useUpdateInfo();

  const validateInputs = (): boolean => {
    if (username && email && password && confirmPassword) {
      const usernameValidation = isValidUsername(username);
      const emailValidation = isValidEmailAddress(email);
      const passwordValidation = isValidPassword(password);
      const confirmPasswordValidation = isValidPassword(confirmPassword);

      if (usernameValidation.isValid === false) {
        Alert.alert("Invalid Username", usernameValidation.message, [
          { text: "OK" },
        ]);
        return false;
      }

      if (emailValidation.isValid === false) {
        Alert.alert("Invalid Email Address", emailValidation.message, [
          { text: "OK" },
        ]);
        return false;
      }

      if (passwordValidation.isValid === false) {
        Alert.alert("Invalid Password", passwordValidation.message, [
          { text: "OK" },
        ]);
        return false;
      }

      if (confirmPasswordValidation.isValid === false) {
        Alert.alert(
          "Invalid Confirm Password",
          confirmPasswordValidation.message,
          [{ text: "OK" }],
        );
        return false;
      }

      if (password !== confirmPassword) {
        Alert.alert(
          "Password Mismatch",
          "The passwords you entered do not match. Please try again.",
          [{ text: "OK" }],
        );
        return false;
      }
    } else {
      if (!username) {
        Alert.alert("Missing Username", "Please enter your username.", [
          { text: "OK" },
        ]);
        return false;
      }
      if (!email) {
        Alert.alert("Missing Email", "Please enter your email.", [
          { text: "OK" },
        ]);
        return false;
      }
      if (!password) {
        Alert.alert("Missing Password", "Please enter your password.", [
          { text: "OK" },
        ]);
        return false;
      }
      if (!confirmPassword) {
        Alert.alert(
          "Missing Confirm Password",
          "Please confirm your password.",
          [{ text: "OK" }],
        );
        return false;
      }
    }
    return true;
  };

  const onSubmit = async (): Promise<void> => {
    setIsLoading(true);

    const isValid = validateInputs();
    if (isValid === false) {
      setIsLoading(false);
      return;
    }

    try {
      // await analytics().logEvent("app_sign_up_attempt");
      const { deviceId } = await getDeviceIdentifier();

      const authService = new AuthService();
      await authService.register({
        username: username,
        password: password,
        email: email,
        deviceId,
      });

      const creds: LoginCredentials = {
        email: email,
        username: username,
        password,
      };

      await setLoginCredentialsInStorage(creds);
      await logDeviceData({
        location,
        updates: updatesInfo,
        forceLogging: true,
      });

      router.replace({
        params: {
          username: username,
          email: email,
        },
        pathname: "/(authentication)/verify-account",
      });
    } catch (error) {
      const apiError = error as ApiError;
      switch (apiError.type) {
        case ApiErrorType.AUTH_USER_NOT_CONFIRMED: {
          router.replace({
            params: { emailAddress: email },
            pathname: "/(authentication)/verify-account",
          });
          break;
        }
        default:
          alertUnexpectedApiError(apiError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <KeyboardAwareScrollView
        style={styles.formContainer}
        enableOnAndroid={true}
        enableAutomaticScroll={true}
        keyboardOpeningTime={0}
      >
        <View style={styles.card}>
          <Text style={styles.title}>Create Account</Text>
          <UsernameWithTooltip />
          <UsernameInput
            value={username}
            onChangeText={setUsername}
            placeholder="Provide your username"
          />
          <Text style={styles.label}>Email</Text>
          <EmailInput
            value={email}
            onChangeText={setEmail}
            placeholder="Enter your email"
          />
          <PasswordWithTooltip />
          <PasswordInput
            value={password}
            onChangeText={setPassword}
            placeholder="Enter your password"
          />
          <Text style={styles.label}>Confirm Password</Text>
          <PasswordInput
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            placeholder="Confirm your password"
          />
          <TermsOfService
            agreedToTerms={agreedToTerms}
            setAgreedToTerms={setAgreedToTerms}
          />
          {isLoading ? (
            <ActivityIndicator size="large" />
          ) : (
            <TouchableOpacity
              testID="sign-up-button"
              style={[
                styles.signInButton,
                !agreedToTerms && styles.signInButtonDisabled,
              ]}
              onPress={onSubmit}
              disabled={!agreedToTerms}
            >
              <Text style={styles.signInButtonText}>Sign up</Text>
            </TouchableOpacity>
          )}
        </View>
      </KeyboardAwareScrollView>
    </View>
  );
}

export default SignupForm;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  formContainer: {
    width: "100%",
    maxWidth: 400,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 10,
    textAlign: "center",
    color: "#4A5568",
  },
  card: {
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    borderRadius: 10,
    padding: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
  },
  label: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 5,
    color: "#4A5568",
  },
  signInButton: {
    backgroundColor: "#4299E1",
    borderRadius: 5,
    padding: 15,
    alignItems: "center",
    marginTop: 10,
  },
  signInButtonDisabled: {
    backgroundColor: "#A0AEC0",
  },
  signInButtonText: {
    color: "white",
    fontSize: 18,
    fontWeight: "600",
  },
});
